<script setup lang="ts">
    // @ts-expect-error raison: lib externe mal typée
    import { computed, defineProps, defineEmits, reactive, ref, toRaw, watch } from 'vue';
    import { AMOUNT_FORMAT_CONFIG, formatAmount } from '@/utils';
    import axios from 'axios';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';

    const emit = defineEmits(['updated']);
    const props = defineProps<{
        startupInfos: any
    }>();
    const queryParams = reactive({
        pageNumber: 1,
        sortDir: 'DESC',
        sortColumn: 'created_at',
        sortOption: 'created_at_desc',
    });
    const pagination = reactive({
        total: 0,
        per_page: 10,
        last_page: 1
    });
    const itemsList = ref([] as any[]);
    const itemsPaginated = ref([] as any[]);
    const errorMessage = ref('');
    const errorMessageValidationPrelevement = ref('');
    const loading = ref(false);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const loadingValidationPrelevement = ref(false);
    const isFormValid = ref(false);
    const formData = reactive({
        periode: null as any | null,
        montant_prelevement: 0,
        file: null as File | null,
        file_name: '',
        file_preview: null as any,
    });
    const errors = reactive({
        montant_prelevement: "",
        file_name: ""
    });
    const periodes_options = ref([] as any[]);
    const collectivite = ref(null as any);
    const statsInfos = ref({
        montant_prelevement_expected: 0,
    });

    watch(() => props.startupInfos, (newVal: any) => {
        const isFirstChanged = !collectivite.value;
        collectivite.value = newVal.collectivite;
        periodes_options.value = newVal.periodes;
        formData.periode = newVal.periodes.find((p: any) => p.selected == true);
        if (!formData.periode) {
            formData.periode = newVal.periodes.length > 0 ? { value: newVal.periodes[0].value, label: newVal.periodes[0].label } : null;
        }
        if (isFirstChanged) {
            doGetPrelevementDetailsInfos();
        }
    }, { immediate: true });

    watch(formData, (oldFormData, newFormData) => {
        validate();
    });
    const prelevement_percentage = computed(() => {
        return formData.montant_prelevement && statsInfos.value.montant_prelevement_expected
            ? Math.min(100, (formData.montant_prelevement / statsInfos.value.montant_prelevement_expected) * 100).toFixed(0)
            : 0;
    });

    function validate() {
        let isValid = true;

        errors.montant_prelevement = '';
        if (!formData.montant_prelevement || formData.montant_prelevement <= 0) {
            errors.montant_prelevement ="Le montant est requis";
            isValid = false;
        }

        errors.file_name = '';
        if (!formData.file_name) {
            errors.file_name ="Le fichier est requis";
            isValid = false;
        }

        isFormValid.value = isValid;
        
        return isValid;
    }

    async function doGetPrelevementDetailsInfos() {
        try {
            loading.value = true;
            errorMessage.value = '';
            const queryParamsRaw = {
                periode: formData.periode?.value || '',
            };
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).map(([k,v]) => [k, String(v)]))).toString();
            const httpResponse = await axios.get(`/backoffice/finance/prelevements/get_prelevements_infos/${collectivite.value.id}?${queryString}`);

            statsInfos.value.montant_prelevement_expected = httpResponse.data.montant_prelevement_expected;

            itemsList.value = httpResponse.data.prelevements;
            pagination.total = itemsList.value.length;
            pagination.last_page = Math.ceil(pagination.total / pagination.per_page);
            onPaginate(queryParams.pageNumber);
        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }

    function onSelectPeriode(event: any) {
        const selectedValue = event.target.value;
        const selectedOption = periodes_options.value.find(option => option.value === selectedValue);
        formData.periode = selectedOption || null;
        doGetPrelevementDetailsInfos();
    }

    function onFileUploaded(event: any) {
        if (event.target.files.length > 0) {
            var reader = new FileReader();
            reader.onload = function(e) {
                formData.file = event.target.files[0];
                formData.file_name = event.target.files[0].name;
                formData.file_preview = e.target!.result as string;
                event.target.value = null;
            }
            reader.readAsDataURL(event.target.files[0]);
        }
    }

    async function doListItems() {
        try {
            loading.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw({ ...queryParams,
                periode: formData.periode.value || '',
            });
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).map(([k,v]) => [k, String(v)]))).toString();
            const httpResponse = await axios.get(`/backoffice/finance/prelevements/list_prelevements/${collectivite.value.id}?${queryString}`);
            itemsList.value = httpResponse.data;
            pagination.total = itemsList.value.length;
            pagination.last_page = Math.ceil(pagination.total / pagination.per_page);
            onPaginate(queryParams.pageNumber);
        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }
    
    function onPaginate(pageNumber: number) {
        queryParams.pageNumber = pageNumber;
        const startIndex = (queryParams.pageNumber - 1) * pagination.per_page;
        const endIndex = startIndex + pagination.per_page;
        itemsPaginated.value = itemsList.value.slice(startIndex, endIndex);
    }

    async function onSubmit() {
        if (!validate()) { return; }
        loading.value = true;
        errorMessage.value = '';
        try {
            const formToUpload = new FormData();
            formToUpload.append("collectivite_id", collectivite.value.id.toString());
            formToUpload.append("periode_prelevement", formData.periode.value);
            formToUpload.append("montant_prelevement", formData.montant_prelevement.toString());
            formToUpload.append("fichier_prelevement", formData.file!, formData.file!.name);
            await axios.post(`/backoffice/finance/prelevements/save_prelevement/${collectivite.value.id}`, formToUpload);
            emit('updated');
            resetForm();
            doListItems();
        } catch(error: any) {
            errorMessage.value = error.message;
            console.error(error);
        } finally {
            loading.value = false;
        }
    }

    function resetForm() {
        formData.montant_prelevement = 0;
        formData.file = null;
        formData.file_name = '';
        formData.file_preview = null;
        isFormValid.value = false;
        errorMessage.value = '';
    }   

    async function doValidatePrelevement(prelevement: any) {
        const confirmation = await confirmationModalRef.value?.open("Prélèvemenets", "Voulez-vous vraiment valider l'élément sélectionné ?");
        if (confirmation) {
            try {
                loadingValidationPrelevement.value = true;
                errorMessageValidationPrelevement.value = '';
                const httpResponse = await axios.post(`/backoffice/finance/prelevements/validate_prelevement/${collectivite.value.id}`, { prelevement_id: prelevement.id });
                emit('updated', httpResponse.data);
                doListItems();
            } catch(error: any) {
                errorMessageValidationPrelevement.value = error.message;
                console.error(error);
            } finally {
                loadingValidationPrelevement.value = false;
            }
        }
    }
</script>
<template>
    <div class="s20 widget px-6 pt-8 pb-2">
        <div class="s10 mt--4 ml--4 mb-5 collapse-open active">
            <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                Gérer les prélèvements 
            </span>
        </div>
        <!-- <div class="s10 mt--5-5 flex-right">
            <button class="btn lg py-3 white2 active">
                <img src="/icons/icon-telecharger.svg" class="btn-icon" alt="" />
                <span>Modèle de fichier csv</span>
            </button>
        </div> -->
        <div class="clear-both h-5"></div>

        <div class="s20 items-center gap-6 px-0 pb-6 collapse-content active">

            <!------>
            <div class="px-5 mt-3">
                <div class="s10">
                    <h2 class="secondary-font gap-4 mt-2 mb-4">
                        Charger le fichier de prélèvement de {{ formData.periode ? formData.periode.label : '' }}
                    </h2>
                    <p class="mb-2">Format de fichier supporté: PDF. Taille max: 50MB.</p>
                </div>

                <h2 class="s10 font-medium  pl-8">
                    Montant à récevoir <br class="mb-4"/><span class="text-4xl font-bold ml-2">{{ formatAmount(statsInfos.montant_prelevement_expected) }} F CFA</span>
                </h2>

                <div class="clear-both h-5"></div>


                <div class="s10 form-field-split my-4">
                    <div class="form-field-floating lg active half">
                        <select class="form-input-select" name="pays" id="pays" :value="formData.periode?.value" @change="onSelectPeriode">
                            <option :value="periode.value" v-for="periode in periodes_options" :key="periode.value">{{ periode.label }}</option>
                        </select>
                        <label for="pays">Sélectionner une période <b class="color-danger">*</b></label>
                    </div>
                    <hr class="hr" />
                    <div class="form-field-floating lg reactive half">
                        <money3 class="form-input" v-model="formData.montant_prelevement" name="montant" id="montant" placeholder="" v-bind="AMOUNT_FORMAT_CONFIG"></money3>
                        <label for="montant">Montant du virement</label>
                        <span class="">F&nbsp;CFA</span>
                    </div>
                </div>
                <div class="s10_mobile pl-8">
                    <!--Upload file-->
                    <label for="file-prelevement" class="mt-4 custom-upload" :class="{'active': formData.file}"><!--active-->
                        <div class="custom-upload-st2">
                            <input id="file-prelevement" class="display-none" type="file" @change="onFileUploaded($event)" accept=".pdf" />
                            <div class="s4 custom-upload-img">.PDF</div>
                            <div class="s12">
                                <span id="file-upload-title" class="custom-upload-text">{{ formData.file_name ? formData.file_name : 'Charger le fichier' }}</span> <b class="color-danger">*</b><br>
                                <span id="file-upload-title" class="custom-upload-text">{{ formData.file_name ? 'Doc chargé' : 'Uploader le doc' }}</span>
                            </div>
                            <div class="s4 right"><img src="/icons/icon-upload.svg" class="custom-upload-icon" /></div>
                        </div>
                    </label>
                    <!------>
                </div>
                <div class="clear-both h-15"></div>
                <!-- <hr/> -->

                <!--Barre de progression - Component-->
                <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                    <div class="widget-floating py-0 text-xl flex-right">{{ prelevement_percentage }}%</div>
                    <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-yellow" :style="{'width': prelevement_percentage + '%'}">
                        <div class="text-3xl font-bold position-absolute">{{ formatAmount(formData.montant_prelevement) }} F / {{ formatAmount(statsInfos.montant_prelevement_expected) }} F</div>
                    </div>
                </div>
                <!--End Barre de progression-->
                <div class="clear-both h-30"></div>
                <div v-if="isFormValid" style="display: flex; flex-direction: row; justify-content: end;">
                    <span v-if="!!errorMessage" class="text-red">{{ errorMessage }}</span>
                    <custom-button type="button" :classesList="'btn lg py-5 black'" :loading="loading" @click="onSubmit">
                        <span>Télécharger le fichier de prélèvement</span>
                    </custom-button>
                </div>
                <div class="clear-both h-15"></div>
            </div>
            <!------>

            <div style="display: flex; flex-direction: row; justify-content: space-between; margin-bottom: 10px;">
                <div class="text-red">{{ errorMessageValidationPrelevement }}</div>
                <button @click="doListItems" class="btn black normal justify-content-center mx-0 btn-filtre">
                    <img src="/icons/icon-scan.svg" class="btn-icon" alt="" />
                    <span>Actualiser</span>
                </button>
            </div>
            <table class="table separator skin1"><!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded) sm  -->
                <thead>
                    <tr>
                        <th class="selection-item"><label class="btn-circle" for="check-list1"><input type="checkbox" name="check-list" id="check-list1" /></label></th>
                        <th></th>
                        <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Période </th>
                        <th><img src="/icons/icon-filter-down.svg" class="size-xxs mt--1" alt="" /> Montant</th>
                        <th><img src="/icons/icon-filter-down.svg" class="size-xxs mt--1" alt="" /> Fichier</th>
                        <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Date</th>
                        <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Effectué par</th>
                        <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Etat</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <template v-if="loading">
                        <tr>
                            <td colspan="9" class="text-center py-5">
                                <div style="display: flex; justify-content: center" v-if="loading">
                                    <div class="loader dark"></div>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <template v-else-if="itemsList.length === 0">
                        <tr>
                            <td colspan="9" class="text-center py-5">
                                Aucune fichier de prélèvement trouvée.
                            </td>
                        </tr>
                    </template>
                    <template v-else>
                        <tr class="text-grey-700 over-effect" v-for="(prelevement, index) in itemsList" :key="'prelevement_' + index"><!-- over-effect active-->
                            <td class="selection-item"><label class="btn-circle" for="check-list1"><input type="checkbox" name="check-list" id="check-list1" /></label></td>
                            <td class="font-black"><img src="/icons/icon-data.svg" class="size-medium " /></td>
                            <!-- Janvier 2026 -->
                            <td class="font-bold text-xl">{{ prelevement.periode_prelevement_formatted }}</td>
                            <td class="font-bold text-xl">{{ formatAmount(prelevement.montant_prelevement) }} F CFA</td>
                            <td class="font-bold"><a>{{ prelevement.fichier_prelevement }}</a></td>
                            <!-- 12 Janvier 2026 10:20 -->
                            <td class="text-sm text-grey-500">{{ prelevement.created_at_formatted }}</td>
                            <td class="text-sm text-grey-500">{{ prelevement.admin.username }} <br/> {{ prelevement.admin.email }}</td>
                            <!-- En cours -->
                            <td class="text-sm text-grey-500"><span class="tag-state warning stroke">{{ prelevement.status_prelevement_label }}</span></td>
                            <td class="flex-right">
                                <custom-button type="button" @click="doValidatePrelevement(prelevement)" :classesList="'btn-ultra'" :loading="loadingValidationPrelevement" v-if="prelevement.status_prelevement == 'pending'">
                                    <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                    <span>Valider</span>
                                </custom-button>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <!--Pagination-->
            <div style="display: flex; flex-direction: row; justify-content: center;">
                <custom-pagination v-model="queryParams.pageNumber" :total="pagination.total" :per-page="pagination.per_page" :last_page="pagination.last_page" @update:modelValue="onPaginate" />
            </div>
            <!---->
        </div>
    </div>

    <div class="clear-both h-5"></div>
    <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
    <div class="clear-both h-5"></div>
    <confirmation-modal ref="confirmationModalRef"></confirmation-modal>
</template>